import { useEffect, useState } from "react";
import { getCanvas } from "./api";
import {
  DR_QUESTIONS,
  PHASE_ORDER,
  findPhase,
  type CanvasSession,
  type FieldAnswer,
  type PhaseCode,
  type ReviewLayer,
} from "./schema";
import { PhaseTracker } from "./PhaseTracker";
import { renderProse } from "./prose";

interface Props {
  sessionId: string;
  onHome: () => void;
  onResume: () => void;
  onOutputs: () => void;
  /** Mid-build edit: jump the flow to (phase, fieldIndex). */
  onEditField?: (phase: PhaseCode, fieldIndex: number) => void;
  /** Completed-canvas edit: open the Phase-10 review at this layer (versioned). */
  onReviewField?: (layer: ReviewLayer) => void;
}

export function SessionReview({ sessionId, onHome, onResume, onOutputs, onEditField, onReviewField }: Props) {
  const [session, setSession] = useState<CanvasSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePrint = () => window.print();
  const handleDownloadJson = () => {
    if (!session) return;
    const blob = new Blob([JSON.stringify(session, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${session.workflowName || "canvas"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    let cancelled = false;
    setSession(null);
    setError(null);
    getCanvas(sessionId)
      .then((s) => {
        if (!cancelled) setSession(s);
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (error) {
    return (
      <div className="canvas">
        <div className="banner banner--error">{error}</div>
        <button className="btn btn--ghost" onClick={onHome}>← Home</button>
      </div>
    );
  }
  if (!session) return <div className="canvas canvas--loading">Loading review…</div>;

  const isComplete = session.currentStage === "COMPLETE";
  const inDR = session.currentStage === "DR" || isComplete;

  // Progress: how many components are filled vs. empty (Pascal July-12 #1).
  const layerCounts = PHASE_ORDER.map((code) => {
    const def = findPhase(code);
    const state = session.phases[code];
    return {
      code,
      total: def.fields.length,
      answered: def.fields.filter((f) => !!state.fields[f.name]).length,
    };
  });
  const totalFields = layerCounts.reduce((n, l) => n + l.total, 0);
  const totalAnswered = layerCounts.reduce((n, l) => n + l.answered, 0);
  const countFor = (code: PhaseCode) => layerCounts.find((l) => l.code === code)!;

  return (
    <div className="canvas">
      <PhaseTracker session={session} />

      <div className="canvas__panel">
        <header className="review__header">
          <div>
            <p className="canvas__eyebrow">Read-only review</p>
            <h1 className="canvas__display">{session.workflowName}</h1>
            <p className="canvas__lede">
              Named owner — {session.namedOwner} ·{" "}
              <span className="mono">{session.currentStage}</span>
            </p>
            <p className="review__progress">
              <strong>{totalAnswered} of {totalFields}</strong> components filled
              {totalAnswered < totalFields && (
                <span className="review__progress-empty"> · {totalFields - totalAnswered} empty</span>
              )}
            </p>
          </div>
          <div className="review__nav">
            <button className="btn btn--text" onClick={handlePrint}>Print</button>
            <button className="btn btn--text" onClick={handleDownloadJson}>Export JSON</button>
            <button className="btn btn--ghost" onClick={onHome}>← Home</button>
            {!isComplete && (
              <button className="btn btn--primary" onClick={onResume}>
                Resume editing →
              </button>
            )}
            {isComplete && (
              <button className="btn btn--primary" onClick={onOutputs}>
                Go to outputs →
              </button>
            )}
          </div>
        </header>

        {PHASE_ORDER.map((code) => {
          const def = findPhase(code);
          const state = session.phases[code];
          const fields = def.fields;
          return (
            <section key={code} className="review__phase">
              <header className="review__phase-header">
                <div>
                  <span className="canvas__phase-code">{code}</span>
                  <span className="canvas__phase-title"> — {def.title}</span>
                </div>
                <div className="review__phase-meta">
                  <span className="mono review__phase-count">
                    {countFor(code).answered}/{countFor(code).total} filled
                  </span>
                  <span className={`stage stage--${state.status}`}>{state.status}</span>
                  {state.qualityScore !== null && (
                    <span className="mono">avg {state.qualityScore.toFixed(2)}/5</span>
                  )}
                </div>
              </header>
              {fields.map((f, idx) => {
                const a = state.fields[f.name];
                return (
                  <ReviewField
                    key={f.name}
                    code={f.code}
                    label={f.label}
                    question={f.question}
                    answer={a}
                    editable={
                      isComplete
                        ? !!onReviewField
                        : state.status !== "locked" && !!onEditField
                    }
                    onEdit={() =>
                      isComplete
                        ? onReviewField?.(code)
                        : onEditField?.(code, idx)
                    }
                  />
                );
              })}
            </section>
          );
        })}

        {inDR && (
          <section className="review__phase">
            <header className="review__phase-header">
              <div>
                <span className="canvas__phase-code">DR</span>
                <span className="canvas__phase-title"> — Deployment Readiness</span>
              </div>
              <div className="review__phase-meta">
                <span className={`stage stage--${session.deploymentReadiness.status}`}>
                  {session.deploymentReadiness.status}
                </span>
              </div>
            </header>
            {DR_QUESTIONS.map((d) => {
              const a = session.deploymentReadiness.answers[d.code];
              return (
                <ReviewField
                  key={d.code}
                  label={d.code}
                  question={d.question}
                  answer={a}
                  editable={isComplete && !!onReviewField}
                  onEdit={() => onReviewField?.("DR")}
                />
              );
            })}
          </section>
        )}

        {session.consistencyChecks.results.length > 0 && (
          <section className="review__phase">
            <header className="review__phase-header">
              <div>
                <span className="canvas__phase-code">CC</span>
                <span className="canvas__phase-title"> — Consistency checks</span>
              </div>
            </header>
            <ul className="checks">
              {session.consistencyChecks.results.map((c) => (
                <li key={c.code} className={`check check--${c.verdict}`}>
                  <span className="check__code mono">{c.code}</span>
                  <div className="check__body">
                    <div className="check__label">{c.label}</div>
                    <div className="check__detail">{c.detail}</div>
                  </div>
                  <span className={`check__verdict check__verdict--${c.verdict}`}>
                    {c.verdict.toUpperCase()}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="canvas__actions">
          <button className="btn btn--ghost" onClick={onHome}>← Home</button>
          {!isComplete ? (
            <button className="btn btn--primary" onClick={onResume}>
              Resume editing →
            </button>
          ) : (
            <button className="btn btn--primary" onClick={onOutputs}>
              Go to outputs →
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function ReviewField({
  code,
  label,
  question,
  answer,
  editable,
  onEdit,
}: {
  code?: string;
  label: string;
  question: string;
  answer: FieldAnswer | undefined;
  editable?: boolean;
  onEdit?: () => void;
}) {
  const labelNode = code ? (
    <>
      <span className="mono">{code}</span> · {label}
    </>
  ) : (
    label
  );
  if (!answer) {
    return (
      <article className="review__field review__field--empty">
        <div className="review__field-head">
          <div className="review__field-label">{labelNode}</div>
          {editable && onEdit && (
            <button className="btn btn--text review__field-edit" onClick={onEdit}>Edit</button>
          )}
        </div>
        <div className="review__field-question">{renderProse(question)}</div>
        <div className="review__field-empty">Not yet answered.</div>
      </article>
    );
  }
  return (
    <article className="review__field">
      <div className="review__field-head">
        <div>
          <div className="review__field-label">{labelNode}</div>
          <div className="review__field-question">{renderProse(question)}</div>
        </div>
        <div className="review__field-score">
          {editable && onEdit && (
            <button className="btn btn--text review__field-edit" onClick={onEdit}>Edit</button>
          )}
          <span className={`eval__score eval__score--${gradeOf(answer.score)}`}>
            <span className="mono">{answer.score}</span>
            <span className="eval__score-of">/5</span>
          </span>
        </div>
      </div>
      <div className="review__field-answer">{answer.answer}</div>
      <div className="review__field-chips">
        <span className="chip chip--neutral">
          <span className="chip__k">Confidence</span>
          <span className="chip__v mono">{answer.confidence}</span>
        </span>
        {answer.gapFlagged ? (
          <span className="chip chip--warn">
            <span className="chip__k">Gap</span>
            <span className="chip__v">{answer.gapNote || "Flagged"}</span>
          </span>
        ) : (
          <span className="chip chip--ok">
            <span className="chip__k">Gap</span>
            <span className="chip__v">None</span>
          </span>
        )}
      </div>
      {answer.evalText && (
        <details className="review__field-eval">
          <summary>Coach evaluation</summary>
          <p>{renderProse(answer.evalText)}</p>
        </details>
      )}
    </article>
  );
}

function gradeOf(score: number): "low" | "mid" | "high" {
  return score >= 4 ? "high" : score >= 3 ? "mid" : "low";
}
